import { useState, useRef, useCallback, useEffect } from "react";
import { v4 as uuid } from "uuid";
import { isEmpty } from "@util/utils";

/**
 * 商品搜尋 Hook
 * 處理搜尋狀態、中文輸入法、防抖和全商品搜尋 API 呼叫
 * @param {Function} getAllMerchandiseDataApi - 全商品搜尋 API 函式
 * @param {Object} currentMenu - 當前選中的分類選單項
 * @returns {Object} 搜尋相關的狀態和方法
 */
export const useProductSearch = (getAllMerchandiseDataApi, currentMenu) => {
	const [searchKeyword, setSearchKeyword] = useState("");
	const [searchKey, setSearchKey] = useState(uuid()); // 用於重置 TextField

	const timeoutRef = useRef(null); // 用於搜尋防抖
	const isOnComposition = useRef(false); // 用於中文輸入法處理

	// 全商品搜尋 API (帶防抖邏輯)
	const searchAllMerchandiseWithDebounce = useCallback(
		async keyword => {
			clearTimeout(timeoutRef.current);

			const trimmedValue = keyword.trim();
			if (isEmpty(trimmedValue)) {
				return Promise.resolve([]); // 關鍵字為空時返回空陣列
			}

			// 防抖處理：延遲 800ms
			return new Promise(resolve => {
				const _t = setTimeout(async () => {
					try {
						const resp = await getAllMerchandiseDataApi({ text: trimmedValue });
						resolve(resp || []);
					} catch (error) {
						console.error("搜尋商品時發生錯誤:", error);
						resolve([]);
					}
				}, 800);

				timeoutRef.current = _t;
			});
		},
		// eslint-disable-next-line
		[getAllMerchandiseDataApi],
	);

	// 處理搜尋輸入變更
	const handleSearchChange = useCallback(
		e => {
			if (e.target instanceof HTMLInputElement && !isOnComposition.current) {
				// 非組字狀態下，觸發搜尋
				const { value } = e.target;
				// 更新關鍵字狀態
				setSearchKeyword(value);
			}
		},
		// eslint-disable-next-line
		[],
	);

	// 處理中文輸入法（統一處理三個事件）
	const handleComposition = useCallback(
		e => {
			if (e.type === "compositionend") {
				// compositionend：組字結束，觸發搜尋
				isOnComposition.current = false;
				handleSearchChange(e);
			} else {
				// compositionstart 或 compositionupdate：標記正在組字
				isOnComposition.current = true;
			}
		},
		// eslint-disable-next-line
		[handleSearchChange],
	);

	// 清理函數：組件卸載時清除防抖計時器
	const clearSearchTimeout = useCallback(
		() => {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		},
		// eslint-disable-next-line
		[],
	);

	// 重置搜尋
	const resetSearch = useCallback(
		() => {
			clearSearchTimeout();
			setSearchKey(uuid());
			setSearchKeyword("");
		},
		// eslint-disable-next-line
		[clearSearchTimeout],
	);

	// 組件卸載時清理計時器
	useEffect(
		() => {
			return () => {
				clearSearchTimeout();
			};
		},
		// eslint-disable-next-line
		[clearSearchTimeout],
	);

	return {
		searchKeyword,
		searchKey,
		handleSearchChange,
		handleComposition,
		resetSearch,
		clearSearchTimeout,
		searchAllMerchandiseWithDebounce,
	};
};
